import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Tiny generic pagination + filter helper. Stateless — caller controls `page`,
 * `pageSize`, and total count. Use with `usePagedList` for the common case.
 *
 * Props:
 *  - total: number of items in the filtered set
 *  - page: 1-indexed current page
 *  - pageSize: items per page
 *  - onPageChange(newPage)
 *  - testid prefix (optional)
 */
export function PagerBar({ total, page, pageSize, onPageChange, testid = 'pager' }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div
      className="flex items-center justify-between gap-3 px-1 py-2 text-sm text-muted-foreground flex-wrap"
      data-testid={testid}
    >
      <span data-testid={`${testid}-summary`}>
        Showing <strong className="text-foreground">{from}–{to}</strong> of <strong className="text-foreground">{total}</strong>
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          data-testid={`${testid}-first`}
          aria-label="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid={`${testid}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-2 text-xs" data-testid={`${testid}-current`}>
          Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong>
        </span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid={`${testid}-next`}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          data-testid={`${testid}-last`}
          aria-label="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Hook: paginate + filter an in-memory list.
 *
 * @param all      - full source array
 * @param matcher  - (item) => boolean (true to keep)
 * @param pageSize - items per page (default 25)
 */
export function usePagedList(all, matcher, pageSize = 25) {
  const [page, setPage] = React.useState(1);
  const filtered = React.useMemo(
    () => (matcher ? all.filter(matcher) : all),
    [all, matcher],
  );
  // Reset to page 1 whenever filter changes the count
  React.useEffect(() => {
    setPage(1);
  }, [filtered.length, matcher]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { items: slice, total: filtered.length, page: safePage, setPage, pageSize, totalPages };
}
